import React, { useState, useEffect } from 'react';
import '../components/DBOSHomepage.css';
import SiteHeader from '../components/SiteHeader';
import './SmartWeb.css';

const SmartTraffic = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    comment: '',
    termsAccepted: false,
    newsletterOptIn: false,
  });
  const [navOpen, setNavOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert('Please accept the terms and privacy policy to continue');
      return;
    }
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you soon.');
    setFormData({
      name: '',
      email: '',
      mobile: '',
      comment: '',
      termsAccepted: false,
      newsletterOptIn: false,
    });
  };


  useEffect(() => {
    const updateOpenStatus = () => {
      const now = new Date();
      const dublinTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Dublin"}));
      const day = dublinTime.getDay();
      const minutes = dublinTime.getHours() * 60 + dublinTime.getMinutes();
      const isOpenNow = (
        (day >= 1 && day <= 4 && minutes >= 540 && minutes < 1080) ||
        (day === 5 && minutes >= 540 && minutes < 720)
      );
      const indicator = document.querySelector('.status-indicator');
      const hoursText = document.querySelector('.hours-text');
      const hoursContainer = document.querySelector('.opening-hours');
      if (indicator && hoursText && hoursContainer) {
        if (isOpenNow) {
          indicator.className = 'status-indicator open';
          hoursText.textContent = 'OPEN';
          hoursContainer.classList.add('status-open');
          hoursContainer.classList.remove('status-closed');
        } else {
          indicator.className = 'status-indicator closed';
          hoursText.textContent = 'CLOSED';
          hoursContainer.classList.remove('status-open');
          hoursContainer.classList.add('status-closed');
        }
      }
    };
    updateOpenStatus();
    const interval = setInterval(updateOpenStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="smartweb-page">
      <SiteHeader active="solutions" />

      <main className="smartweb-content">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="container">
            <h1 className="page-title">
              Fix Your Broken Windows.
              <span className="gold-gradient shimmering"> Get Found Online.</span>
            </h1>
            <p className="hero-lead shimmering">
              Clarity → Consistency → Trust → Visibility → Conversion
            </p>
          </div>
        </section>

        {/* The Problem */}
        <section className="content-section">
          <div className="container">
            <h2>The Digital Broken Window Problem</h2>
            <p>
              You've invested in your business. New equipment. Professional signage. A beautiful shopfront. 
              But online? Your Google Business Profile shows opening hours from 2022. Your phone number on 
              Facebook is different from your website. Your Instagram says you're in Dublin 2, but Google 
              Maps says Dublin 1. Your homepage hasn't been updated since 2019.
            </p>
            <p>
              This is the Broken Window Principle in action. Remember the theory? A building with one broken 
              window that goes unfixed signals neglect. Soon, more windows break. Vandalism increases. 
              The entire neighborhood declines—all because one small problem was ignored.
            </p>
            <p>
              Your digital presence works the same way. One wrong opening hour. One SSL certificate error. 
              One product listing from 2015. One broken link. Suddenly, all your investment—the fancy 
              equipment, the professional photos, the premium service—means nothing. The customer sees that 
              broken window and thinks: "This business probably closed. I'll go somewhere else."
            </p>
            <p>
              Google sees it too. Inconsistent NAP data (Name, Address, Phone) across directories. Outdated 
              content. Disconnected frontend and backend systems. Google's algorithm interprets these signals 
              as a lack of trust. Your search rankings drop. New customers searching for exactly what you 
              offer drive right past you to your competitor—who fixed their windows.
            </p>
            <p>
              Here's the harder truth: Facebook and Instagram reach your existing fans. They don't reach 
              the family that just moved to town. The visiting relative searching for "best café near me." 
              The young professional looking for a reliable service provider. These people search on Google. 
              They look at your Google Business Profile. They check if your website loads quickly and looks 
              current. And if they see broken windows? They're gone.
            </p>
            <p>
              This is why so many established businesses can't find successors. The older generation built 
              the business when foot traffic mattered. But the younger generation lives online. If you're 
              not visible there—if your digital storefront is covered in dust, broken links, and outdated 
              information—they'll never discover you in the first place.
            </p>
          </div>
        </section>

        {/* The Solution */}
        <section className="content-section solution-section">
          <div className="container">
            <h2>The SmartTraffic Solution</h2>
            <p className="lead-text">
              SmartTraffic fixes your broken windows. We audit every corner of your digital presence, 
              identify the inconsistencies killing your credibility, and restore trust across every 
              platform where customers find you.
            </p>
            <p>
              We start with what matters most: NAP consistency. Your Name, Address, and Phone number 
              must be identical everywhere—Google Business Profile, Facebook, Instagram, your website, 
              online directories, review sites. One mismatch and Google's algorithm flags you as 
              unreliable. We find every discrepancy and fix it.
            </p>
            <p>
              Then we look at your signals. Are your opening hours correct and current? Is your SSL 
              certificate valid? Do your product listings reflect what you actually offer? Are images 
              recent or from 2015? Is your homepage optimized for local search and AI-powered search 
              engines? We systematically address each broken window.
            </p>
            <p>
              The goal isn't just to fix what's broken. It's to send trust signals so strong that 
              Google elevates your visibility. New customers find you. Your local search rankings 
              improve. People who never knew you existed start walking through your door—or calling, 
              booking, buying online.
            </p>
            <p>
              This is the framework: <strong>Clarity → Consistency → Trust → Visibility → Conversion.</strong> 
              We clarify your digital presence. We make it consistent everywhere. That consistency builds 
              trust with both customers and search algorithms. Trust creates visibility. Visibility drives 
              conversions.
            </p>
          </div>
        </section>

        {/* The Framework */}
        <section className="content-section">
          <div className="container">
            <h2>The SmartTraffic Framework</h2>
            <div className="framework-flow">
              <div className="framework-step">
                <div className="framework-number">1</div>
                <h3>Clarity</h3>
                <p>Understand what customers see when they search for you. Audit every touchpoint.</p>
              </div>
              <div className="framework-arrow">→</div>
              <div className="framework-step">
                <div className="framework-number">2</div>
                <h3>Consistency</h3>
                <p>Fix NAP discrepancies. Update outdated information. Align all platforms.</p>
              </div>
              <div className="framework-arrow">→</div>
              <div className="framework-step">
                <div className="framework-number">3</div>
                <h3>Trust</h3>
                <p>Send strong trust signals to Google and customers. No broken windows.</p>
              </div>
              <div className="framework-arrow">→</div>
              <div className="framework-step">
                <div className="framework-number">4</div>
                <h3>Visibility</h3>
                <p>Improved rankings. More reach. New audiences discover you.</p>
              </div>
              <div className="framework-arrow">→</div>
              <div className="framework-step">
                <div className="framework-number">5</div>
                <h3>Conversion</h3>
                <p>Visibility becomes customers. Clicks become calls, bookings, sales.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="features-section">
          <div className="container">
            <h2>What SmartTraffic Does</h2>
            <div className="features-grid">
              
              <div className="feature-card">
                <h3>NAP Consistency Audit</h3>
                <p>
                  We scan every directory, social profile, review site, and listing where your 
                  business appears. Name, Address, Phone—if it's inconsistent anywhere, we find it. 
                  Then we fix it.
                </p>
                <p>
                  Google's algorithm cross-references your business information across hundreds of 
                  sources. One mismatch flags you as unreliable. We eliminate every discrepancy so 
                  Google trusts you—and ranks you higher.
                </p>
                <ul>
                  <li>Google Business Profile verification</li>
                  <li>Directory listing cleanup</li>
                  <li>Social media profile alignment</li>
                  <li>Review site accuracy check</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Local & AI Search Optimization</h3>
                <p>
                  Most local searches happen on mobile. "Near me" queries. Voice search. AI-powered 
                  assistants recommending businesses. If your site isn't optimized for these, you're 
                  invisible to a huge portion of your potential customers.
                </p>
                <p>
                  We optimize your homepage and Google Business Profile for local search rankings 
                  and AI discoverability. Structured data, schema markup, mobile speed, location 
                  signals—everything that tells search engines you're the right answer.
                </p>
                <ul>
                  <li>Local keyword optimization</li>
                  <li>Schema markup implementation</li>
                  <li>Mobile speed optimization</li>
                  <li>Voice search readiness</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Trust Signal Repair</h3>
                <p>
                  Broken windows destroy trust instantly. We identify and fix every trust-killing 
                  element: SSL errors, outdated content, broken links, incorrect opening hours, 
                  products from 2015, frontend-backend disconnects.
                </p>
                <p>
                  Trust signals work both ways. Customers see current information and feel confident. 
                  Google's algorithm sees consistent, up-to-date content and rewards you with better 
                  rankings.
                </p>
                <ul>
                  <li>SSL certificate validation</li>
                  <li>Content freshness updates</li>
                  <li>Broken link identification and repair</li>
                  <li>Opening hours synchronization</li>
                </ul>
              </div>

              <div className="feature-card">
                <h3>Visibility Beyond Fans</h3>
                <p>
                  Facebook and Instagram are great for engaging existing fans. But they don't reach 
                  new people moving into your area. The family visiting from out of town. The young 
                  professional searching for a reliable provider.
                </p>
                <p>
                  SmartTraffic focuses on visibility where discovery happens: Google Search, Google 
                  Maps, local directories, review platforms. We make sure when someone searches for 
                  what you offer, they find you—even if they've never heard of you before.
                </p>
                <ul>
                  <li>Google Maps optimization</li>
                  <li>Review platform visibility</li>
                  <li>Local directory presence</li>
                  <li>Discovery-focused search strategy</li>
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* What We Don't Do Yet */}
        <section className="content-section">
          <div className="container">
            <h2>What SmartTraffic Doesn't Include (Yet)</h2>
            <p>
              SmartTraffic focuses on fixing the foundation—the broken windows that kill trust and 
              visibility. Once your foundation is solid, you might want advanced analytics, paid 
              advertising campaigns, or sophisticated conversion tracking.
            </p>
            <p>
              That's <strong>SmartPay</strong>—a different service for businesses ready to invest 
              in paid acquisition. But here's the truth: if your NAP data is inconsistent, your SSL 
              is broken, and your opening hours are wrong, paid ads just drive people to broken 
              windows. Fix the foundation first. Then scale.
            </p>
            <ul className="capabilities-list">
              <li><strong>Advanced Analytics:</strong> Available with SmartPay</li>
              <li><strong>Paid Advertising:</strong> Google Ads, Facebook Ads—comes later</li>
              <li><strong>Conversion Tracking:</strong> Detailed attribution—SmartPay territory</li>
            </ul>
            <p>
              First, we fix what's broken. Then, we help you grow.
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="content-section how-it-works">
          <div className="container">
            <h2>How SmartTraffic Works</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Digital Health Assessment</h3>
                <p>
                  We audit your entire digital footprint. Google Business Profile, website, social 
                  media, directories, review sites. We document every broken window.
                </p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Fix the Broken Windows</h3>
                <p>
                  NAP consistency. Trust signals. Opening hours. SSL certificates. Outdated content. 
                  We systematically repair every issue killing your visibility.
                </p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Monitor & Maintain</h3>
                <p>
                  Trust isn't one-and-done. We monitor your digital presence, catch new issues early, 
                  and keep your signals strong so you stay visible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="content-section faq-section">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What is NAP and why does it matter?</h3>
                <p>
                  NAP stands for Name, Address, Phone. Google cross-references your business information 
                  across hundreds of sources. Inconsistencies signal unreliability and hurt your rankings.
                </p>
              </div>
              <div className="faq-item">
                <h3>How long does it take to see results?</h3>
                <p>
                  Some fixes—like correcting Google Business Profile hours—show immediate impact. Full 
                  SEO improvements typically take 2-3 months as search engines re-index your corrected data.
                </p>
              </div>
              <div className="faq-item">
                <h3>Do I need SmartWeb and SmartTraffic?</h3>
                <p>
                  They complement each other. SmartWeb gives you a fast, modern website. SmartTraffic 
                  makes sure people can find it. Together, they build trust and drive conversions.
                </p>
              </div>
              <div className="faq-item">
                <h3>What about Facebook and Instagram?</h3>
                <p>
                  Social media is great for engaging existing customers. SmartTraffic focuses on discovery—
                  helping new people find you when they search Google for what you offer.
                </p>
              </div>
              <div className="faq-item">
                <h3>Can I do this myself?</h3>
                <p>
                  You can, but it's time-consuming and easy to miss things. We know where to look, what 
                  signals matter most, and how to fix issues correctly the first time.
                </p>
              </div>
              <div className="faq-item">
                <h3>What if I want paid advertising later?</h3>
                <p>
                  That's SmartPay. But paid ads work best when your foundation is solid. Fix the broken 
                  windows first, then scale with advertising.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <h2>Ready to Fix Your Broken Windows?</h2>
            <p className="cta-lead">
              Start with your Digital Health Assessment. We'll audit your digital presence and show 
              you exactly what's hurting your visibility.
            </p>
            
            <form className="assessment-form" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="mobile">Mobile Number</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="comment">Tell Us About Your Situation</label>
                <textarea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  maxLength={1500}
                  rows={5}
                  className="form-textarea"
                  placeholder="What challenges are you facing with local visibility? (Optional, max 1500 characters)"
                />
                <div className="char-count">{formData.comment.length} / 1500</div>
              </div>
              
              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    required
                  />
                  <span>I accept the <a href="/legal/terms">terms</a> & <a href="/legal/privacy">privacy policy</a></span>
                </label>
              </div>
              
              <button type="submit" className="btn-primary shimmering-btn">
                Start Growing Your Audience
                <span className="btn-arrow">→</span>
              </button>
            </form>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container">
          <p className="copyright">© 2026 DBOS. Ireland (CRO Registered). All rights reserved.</p>
          <div className="footer-contact">
            <a href="mailto:hello@dbos.world">hello@dbos.world</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartTraffic;
